import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { environment } from "../../../environments/environment";
import { DocumentRequirement } from "../../models/document-requirement";
import { FileMetadata } from "../../models/file-metadata";
import { Requirement } from "../../models/requirement";
import { Token } from "../../models/token";
import { RequirementControllerService } from "../../services/requirement-controller/requirement-controller.service";
import { ServiceControllerService } from "../../services/service-controller/service-controller.service";
import { Store } from "../../services/store.service";
import {TranslatedBaseError} from "../pages/TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: "document-edit",
  templateUrl: "./document-edit.component.html",
  styles: [],
  providers: [RequirementControllerService, ServiceControllerService, ToastrService],
})
export class DocumentEditComponent extends TranslatedBaseError implements OnChanges {

  @Input() public serviceId: string;
  @Input() public dragIcons: boolean = true;
  @Input() public requirement: Requirement;
  @Input() public documentRequirement: DocumentRequirement;
  @Output() public formSubmit = new EventEmitter();
  public downloadLink: string;
  public fileMetadata: FileMetadata;
  public hasBaseDropZoneOver: boolean;
  public pending: boolean;
  public name: any;
  private baseUrl: string = environment.BASE_URL;
  private sayReason: boolean = false;
  // file upload settings
  private sizeLimit = 1048576;
  public isLoading: boolean = false;

  constructor(private requirementService: RequirementControllerService,
              private serviceController: ServiceControllerService,
              public store: Store,
              public toastr: ToastrService,
              public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (changes.serviceId && changes.serviceId.currentValue) {
      this.serviceId = changes.serviceId.currentValue;
      const token = localStorage.getItem(Token.tokeNameInSotage);
    }
    if (changes.requirement) {
      if (changes.requirement.currentValue) {
        this.requirement = changes.requirement.currentValue;
        if (this.requirement.fileId !== undefined) {
          this.uploadFileData();
        } else {
          this.downloadLink = undefined;
        }
      }
    }
  }

  public save() {
    if (!this.isLoading) {
      this.isLoading = true;
      if (this.requirement.id === undefined) {
        this.createRequirement(this.requirement);
      } else {
        this.saveRequirement(this.requirement);
      }
    }
  }

  public cancel() {
    this.formSubmit.emit({
      save: false, cancel: true,
    });
  }

  public createRequirement(requirement: Requirement) {
    this.requirementService.createRequirement(requirement).subscribe(
      (data) => {
        this.formSubmit.emit({ requirement: data, create: true });
        this.sayReason = false;
        this.responseSuccessful("Data is saved!");
      },
      (error) => this.showErrorMessage(error),
      () => this.isLoading = false,
    );
  }

  public saveRequirement(requirement: Requirement) {
    this.requirementService.saveRequirement(requirement).subscribe(
      (data) => {
        this.formSubmit.emit({
          save: true,
          requirement,
          documentRequirement: this.documentRequirement,
        });
        this.sayReason = false;
        this.responseSuccessful("Data is saved!");
      },
      (error) => this.showErrorMessage(error),
      () => this.isLoading = false,
    );
  }

  public saveRequirementThatFileIsDeleted(requirement: Requirement) {
    this.requirementService.saveRequirement(requirement).subscribe(
      (data) => this.uploadFileData(),
      (error) => this.showErrorMessageWithTitle("Requirement save error! ", error),
    );
  }

  public deleteRequirement(isConfirmed: boolean) {
    if (isConfirmed) {
      this.requirementService.deleteRequirement(this.requirement.id).subscribe(
        (data) => {
          this.formSubmit.emit({ save: false, delete: true, requirement: undefined });
          this.sayReason = false;
          this.responseSuccessful("Data is deleted!");
        },
        (error) => this.showErrorMessageWithTitle("Requirement delete error! ", error),
      );
    }
  }

  public uploadFileData() {
    if (this.requirement && this.requirement.fileId) {
      this.downloadLink = this.baseUrl + "service/" + this.serviceId + "/file/" + this.requirement.fileId + "/binary";
      this.getFileMetadata(this.serviceId, this.requirement.fileId);
    } else {
      this.downloadLink = undefined;
      this.fileMetadata = undefined;
    }
  }

  public getFileMetadata(serviceId: string, fileId: string) {
    this.serviceController.getServiceFile(serviceId, fileId).subscribe(
      (data: FileMetadata) => this.fileMetadata = data,
      (error) => this.showErrorMessageWithTitle("Get file error!", error),
    );
  }

  public deleteFile(isConfirmed: boolean) {
    if (isConfirmed && this.requirement.fileId) {
      this.serviceController.deleteServiceFile(this.serviceId, this.requirement.fileId).subscribe(
        (data) => {
          this.requirement.fileId = undefined;
          this.saveRequirementThatFileIsDeleted(this.requirement);
        },
        (error) => this.showErrorMessageWithTitle("File delete error!", error),
      );
    }
  }

  // file upload settings
  // https://github.com/jkuri/ng2-uploader
  public handleUpload(data: any) {
    setTimeout(() => {
      if (data && data.response) {
        this.requirement.fileId = data.response;
        if (this.requirement.fileId !== undefined) {
          this.uploadFileData();
        } else {
          this.downloadLink = undefined;
        }
      }
    });
  }

  public download() {
    return this.serviceController.downloadServiceFile(this.serviceId, this.requirement.fileId).subscribe((res: any) => {
      const pdfUrl = (window.URL || (window as any).webkitURL).createObjectURL(new Blob([res], { type: this.fileMetadata.contentType }));
      const anchor = document.createElement("a");
      anchor.href = pdfUrl;
      anchor.setAttribute("download", this.fileMetadata.filename);
      anchor.click();
    });
  }

  public fileOverBase(e: boolean) {
    this.hasBaseDropZoneOver = e;
  }

  public beforeUpload(uploadingFile): void {
    if (uploadingFile.size > this.sizeLimit) {
      uploadingFile.setAbort();
      alert("File is too large");
    }
  }

}
